// src/features/admin/adminJobSlice.js
import axios from '../../utils/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lifecycleError } from '../../utils/bookingLifecycle';
import { dedupeEntities } from '../../utils/collectionUtils';

// ────────────────────────────────────────────────
// Thunks
// ────────────────────────────────────────────────

export const getAllJobs = createAsyncThunk(
  'adminJob/getAllJobs',
  async ({ page = 1, limit = 20, active, isOpen, businessId, search }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (active !== undefined) params.active = active;
      if (isOpen !== undefined) params.isOpen = isOpen;
      if (businessId) params.businessId = businessId;
      if (search) params.search = search;

      const res = await axios.get(`/jobs/admin/jobs`, { params });
      return res.data.data || res.data; // expected: { jobs, pagination: {page, limit, total, pages} }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch jobs');
    }
  }
);

export const getSingleJob = createAsyncThunk(
  'adminJob/getSingleJob',
  async (jobId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/jobs/admin/jobs/${jobId}`);
      // Adjust according to your real API response structure
      return res.data.data?.job || res.data.job || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch single job details'
      );
    }
  }
);

export const toggleJobActive = createAsyncThunk(
  'adminJob/toggleJobActive',
  async ({ jobId, active }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/jobs/admin/jobs/${jobId}/toggle-active`, { active });
      return res.data.data?.job || res.data.job || res.data;
    } catch (err) {
      return rejectWithValue(
        lifecycleError(err, 'Failed to toggle job active status')
      );
    }
  }
);

export const toggleJobOpen = createAsyncThunk(
  'adminJob/toggleJobOpen',
  async (jobId, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/jobs/admin/jobs/${jobId}/toggle-open`);
      return res.data.data?.job || res.data.job || res.data;
    } catch (err) {
      return rejectWithValue(
        lifecycleError(err, 'Failed to toggle job open status')
      );
    }
  }
);

export const deleteJob = createAsyncThunk(
  'adminJob/deleteJob',
  async (jobId, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`/jobs/admin/jobs/${jobId}`);
      return { jobId, message: res.data.message || 'Job deleted successfully' };
    } catch (err) {
      return rejectWithValue(lifecycleError(err, 'Failed to delete job'));
    }
  }
);

export const getJobStats = createAsyncThunk(
  'adminJob/getJobStats',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/jobs/admin/stats`);
      return res.data.data?.stats || res.data.stats || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch job statistics');
    }
  }
);

export const getBusinessJobsAndStats = createAsyncThunk(
  'adminJob/getBusinessJobsAndStats',
  async (businessId, { rejectWithValue }) => {
    try {
      const [jobsRes, statsRes] = await Promise.all([
        axios.get(`/jobs/admin/business/${businessId}/jobs`, { params: { limit: 50 } }),
        axios.get(`/jobs/admin/business/${businessId}/stats`),
      ]);

      return {
        jobs: jobsRes.data.data?.jobs || jobsRes.data.jobs || [],
        stats: statsRes.data.data?.stats || statsRes.data.stats,
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch business jobs and stats'
      );
    }
  }
);

// ────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────

const initialState = {
  jobs: [],
  currentJob: null,           // ← single job detail
  total: 0,
  currentPage: 1,
  stats: null,
  businessStats: null,
  businessJobs: [],
  loading: false,
  actionLoading: false,
  error: null,
  lastActionJobId: null,
};

const adminJobSlice = createSlice({
  name: 'adminJob',
  initialState,
  reducers: {
    clearAdminJobError: (state) => {
      state.error = null;
    },
    clearCurrentJob: (state) => {
      state.currentJob = null;
    },
  },
  extraReducers: (builder) => {
    // getAllJobs
    builder
      .addCase(getAllJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = dedupeEntities(action.payload.jobs);
        state.total = action.payload.pagination?.total || action.payload.total || 0;
        state.currentPage = action.payload.pagination?.page || 1;
      })
      .addCase(getAllJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // getSingleJob
    builder
      .addCase(getSingleJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSingleJob.fulfilled, (state, action) => {
        state.loading = false;
        state.currentJob = action.payload;
      })
      .addCase(getSingleJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentJob = null;
      });

    // toggleJobActive
    builder
      .addCase(toggleJobActive.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionJobId = action.meta.arg.jobId;
      })
      .addCase(toggleJobActive.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        // Update list if present
        const index = state.jobs.findIndex((j) => j._id === updated._id);
        if (index !== -1) {
          state.jobs[index] = { ...state.jobs[index], ...updated };
        }
        // Update currentJob if viewing detail
        if (state.currentJob?._id === updated._id) {
          state.currentJob = { ...state.currentJob, ...updated };
        }
        state.lastActionJobId = null;
      })
      .addCase(toggleJobActive.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionJobId = null;
      });

    // toggleJobOpen
    builder
      .addCase(toggleJobOpen.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionJobId = action.meta.arg;
      })
      .addCase(toggleJobOpen.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const index = state.jobs.findIndex((j) => j._id === updated._id);
        if (index !== -1) {
          state.jobs[index] = { ...state.jobs[index], ...updated };
        }
        if (state.currentJob?._id === updated._id) {
          state.currentJob = { ...state.currentJob, ...updated };
        }
        state.lastActionJobId = null;
      })
      .addCase(toggleJobOpen.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionJobId = null;
      });

    // deleteJob
    builder
      .addCase(deleteJob.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionJobId = action.meta.arg;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.jobs = state.jobs.filter((j) => j._id !== action.payload.jobId);
        if (state.total > 0) state.total -= 1;
        if (state.currentJob?._id === action.payload.jobId) {
          state.currentJob = null;
        }
        state.lastActionJobId = null;
      })
      .addCase(deleteJob.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionJobId = null;
      });

    // getJobStats
    builder.addCase(getJobStats.fulfilled, (state, action) => {
      state.stats = action.payload;
    });

    // getBusinessJobsAndStats
    builder.addCase(getBusinessJobsAndStats.fulfilled, (state, action) => {
      state.businessJobs = dedupeEntities(action.payload.jobs);
      state.businessStats = action.payload.stats;
    });
  },
});

export const { clearAdminJobError, clearCurrentJob } = adminJobSlice.actions;

// ────────────────────────────────────────────────
// Selectors
// ────────────────────────────────────────────────

export const selectAdminJobs = (state) => state.adminJob.jobs;
export const selectAdminJobTotal = (state) => state.adminJob.total;
export const selectAdminJobLoading = (state) => state.adminJob.loading;
export const selectAdminJobError = (state) => state.adminJob.error;
export const selectAdminJobStats = (state) => state.adminJob.stats;

export const selectCurrentJob = (state) => state.adminJob.currentJob;
export const selectJobLoading = (state) => state.adminJob.loading;
export const selectJobError = (state) => state.adminJob.error;

export const selectBusinessJobs = (state) => state.adminJob.businessJobs;
export const selectBusinessStats = (state) => state.adminJob.businessStats;

export default adminJobSlice.reducer;

// // src/features/admin/adminJobSlice.js
// import axios from '../../utils/axios';
// import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// export const getAllJobs = createAsyncThunk(
//   'adminJob/getAllJobs',
//   async ({ page = 1, limit = 20, active, isOpen, businessId, search }, { rejectWithValue }) => {
//     try {
//       const params = { page, limit };
//       if (active !== undefined) params.active = active;
//       if (isOpen !== undefined) params.isOpen = isOpen;
//       if (businessId) params.businessId = businessId;
//       if (search) params.search = search; // if backend supports job title/description search

//       const res = await axios.get(`/jobs/admin/jobs`, { params });
//       return res.data.data || res.data; // expecting { jobs, pagination: {page, limit, total, pages} }
//     } catch (err) {
//       return rejectWithValue(err.response?.data?.message || 'Failed to fetch jobs');
//     }
//   }
// );

// export const getSingleJob = createAsyncThunk(
//   'adminJob/getSingleJob',
//   async (jobId, { rejectWithValue }) => {
//     try {
//       const res = await axios.get(`/jobs/admin/jobs/${jobId}`);
//       // Adjust according to your real API response structure
//       return res.data.data?.job || res.data.job || res.data;
//     } catch (err) {
//       return rejectWithValue(
//         err.response?.data?.message || 'Failed to fetch single job details'
//       );
//     }
//   }
// );
// /**
//  * Get single job details (admin view)
//  */
// export const getJobById = createAsyncThunk(
//   'adminJob/getJobById',
//   async (jobId, { rejectWithValue }) => {
//     try {
//       const res = await axios.get(`/jobs/admin/jobs/${jobId}`);
//       return res.data.data?.job || res.data.job;
//     } catch (err) {
//       return rejectWithValue(err.response?.data?.message || 'Failed to fetch job details');
//     }
//   }
// );

// /**
//  * Toggle job active status (active ↔ inactive)
//  */
// export const toggleJobActive = createAsyncThunk(
//   'adminJob/toggleJobActive',
//   async ({ jobId, active }, { rejectWithValue }) => {
//     try {
//       const res = await axios.patch(`/jobs/admin/jobs/${jobId}/toggle-active`, { active });
//       return res.data.data?.job || res.data.job;
//     } catch (err) {
//       return rejectWithValue(lifecycleError(err, 'Failed to toggle job active status'));
//     }
//   }
// );

// /**
//  * Toggle job open/closed status (isOpen ↔ !isOpen)
//  */
// export const toggleJobOpen = createAsyncThunk(
//   'adminJob/toggleJobOpen',
//   async (jobId, { rejectWithValue }) => {
//     try {
//       const res = await axios.patch(`/jobs/admin/jobs/${jobId}/toggle-open`);
//       return res.data.data?.job || res.data.job;
//     } catch (err) {
//       return rejectWithValue(lifecycleError(err, 'Failed to toggle job open status'));
//     }
//   }
// );

// /**
//  * Delete job (force delete by admin)
//  */
// export const deleteJob = createAsyncThunk(
//   'adminJob/deleteJob',
//   async (jobId, { rejectWithValue }) => {
//     try {
//       const res = await axios.delete(`/jobs/admin/jobs/${jobId}`);
//       return { jobId, message: res.data.message };
//     } catch (err) {
//       return rejectWithValue(lifecycleError(err, 'Failed to delete job'));
//     }
//   }
// );

// /**
//  * Get global job statistics
//  */
// export const getJobStats = createAsyncThunk(
//   'adminJob/getJobStats',
//   async (_, { rejectWithValue }) => {
//     try {
//       const res = await axios.get(`/jobs/admin/stats`);
//       return res.data.data?.stats || res.data.stats;
//     } catch (err) {
//       return rejectWithValue(err.response?.data?.message || 'Failed to fetch job statistics');
//     }
//   }
// );

// /**
//  * Get jobs + stats for a specific business owner
//  */
// export const getBusinessJobsAndStats = createAsyncThunk(
//   'adminJob/getBusinessJobsAndStats',
//   async (businessId, { rejectWithValue }) => {
//     try {
//       const [jobsRes, statsRes] = await Promise.all([
//         axios.get(`/jobs/admin/business/${businessId}/jobs`, { params: { limit: 50 } }),
//         axios.get(`/jobs/admin/business/${businessId}/stats`),
//       ]);

//       return {
//         jobs: jobsRes.data.data?.jobs || jobsRes.data.jobs || [],
//         stats: statsRes.data.data?.stats || statsRes.data.stats,
//       };
//     } catch (err) {
//       return rejectWithValue(err.response?.data?.message || 'Failed to fetch business jobs/stats');
//     }
//   }
// );

// // ────────────────────────────────────────────────
// // Slice
// // ────────────────────────────────────────────────

// const initialState = {
//   jobs: [],
//   currentJob: null,
//   total: 0,
//   currentPage: 1,
//   stats: null,                    // global stats
//   businessStats: null,            // when viewing specific business
//   businessJobs: [],               // jobs of selected business
//   loading: false,
//   actionLoading: false,           // for toggle/delete actions
//   error: null,
//   lastActionJobId: null,
// };

// const adminJobSlice = createSlice({
//   name: 'adminJob',
//   initialState,
//   reducers: {
//     clearAdminJobError: (state) => {
//       state.error = null;
//     },
//     clearCurrentJob: (state) => {
//       state.currentJob = null;
//     },
//   },
//   extraReducers: (builder) => {
//     // ── getAllJobs ───────────────────────────────────────
//     builder
//       .addCase(getAllJobs.pending, (state) => {
//         state.loading = true;
//         state.error = null;
//       })
//       .addCase(getAllJobs.fulfilled, (state, action) => {
//         state.loading = false;
//         state.jobs = action.payload.jobs || [];
//         state.total = action.payload.pagination?.total || 0;
//         state.currentPage = action.payload.pagination?.page || 1;
//       })
//       .addCase(getAllJobs.rejected, (state, action) => {
//         state.loading = false;
//         state.error = action.payload;
//       });

//     // ── getJobById ───────────────────────────────────────
//     builder
//       .addCase(getJobById.pending, (state) => {
//         state.loading = true;
//       })
//       .addCase(getJobById.fulfilled, (state, action) => {
//         state.loading = false;
//         state.currentJob = action.payload;
//       })
//       .addCase(getJobById.rejected, (state, action) => {
//         state.loading = false;
//         state.error = action.payload;
//       });

//     // ── toggleJobActive ──────────────────────────────────
//     builder
//       .addCase(toggleJobActive.pending, (state, action) => {
//         state.actionLoading = true;
//         state.lastActionJobId = action.meta.arg.jobId;
//       })
//       .addCase(toggleJobActive.fulfilled, (state, action) => {
//         state.actionLoading = false;
//         const updated = action.payload;
//         const index = state.jobs.findIndex(j => j._id === updated._id);
//         if (index !== -1) {
//           state.jobs[index] = { ...state.jobs[index], ...updated };
//         }
//         if (state.currentJob?._id === updated._id) {
//           state.currentJob = { ...state.currentJob, ...updated };
//         }
//         state.lastActionJobId = null;
//       })
//       .addCase(toggleJobActive.rejected, (state) => {
//         state.actionLoading = false;
//         state.lastActionJobId = null;
//       });

//     // ── toggleJobOpen ────────────────────────────────────
//     builder
//       .addCase(toggleJobOpen.pending, (state, action) => {
//         state.actionLoading = true;
//         state.lastActionJobId = action.meta.arg;
//       })
//       .addCase(toggleJobOpen.fulfilled, (state, action) => {
//         state.actionLoading = false;
//         const updated = action.payload;
//         const index = state.jobs.findIndex(j => j._id === updated._id);
//         if (index !== -1) {
//           state.jobs[index] = { ...state.jobs[index], ...updated };
//         }
//         if (state.currentJob?._id === updated._id) {
//           state.currentJob = { ...state.currentJob, ...updated };
//         }
//         state.lastActionJobId = null;
//       })
//       .addCase(toggleJobOpen.rejected, (state) => {
//         state.actionLoading = false;
//         state.lastActionJobId = null;
//       });

//     // ── deleteJob ────────────────────────────────────────
//     builder
//       .addCase(deleteJob.pending, (state, action) => {
//         state.actionLoading = true;
//         state.lastActionJobId = action.meta.arg;
//       })
//       .addCase(deleteJob.fulfilled, (state, action) => {
//         state.actionLoading = false;
//         state.jobs = state.jobs.filter(j => j._id !== action.payload.jobId);
//         state.total -= 1;
//         state.lastActionJobId = null;
//       })
//       .addCase(deleteJob.rejected, (state) => {
//         state.actionLoading = false;
//         state.lastActionJobId = null;
//       });

//     // ── getJobStats (global) ─────────────────────────────
//     builder
//       .addCase(getJobStats.fulfilled, (state, action) => {
//         state.stats = action.payload;
//       });

//     // ── getBusinessJobsAndStats ──────────────────────────
//     builder
//       .addCase(getBusinessJobsAndStats.fulfilled, (state, action) => {
//         state.businessJobs = action.payload.jobs;
//         state.businessStats = action.payload.stats;
//       });
//   },
// });

// export const { clearAdminJobError, clearCurrentJob } = adminJobSlice.actions;

// export const selectAdminJobs = (state) => state.adminJob.jobs;
// export const selectAdminJobTotal = (state) => state.adminJob.total;
// export const selectAdminJobLoading = (state) => state.adminJob.loading;
// export const selectAdminJobError = (state) => state.adminJob.error;
// export const selectCurrentAdminJob = (state) => state.adminJob.currentJob;
// export const selectAdminJobStats = (state) => state.adminJob.stats;
// export const selectBusinessJobs = (state) => state.adminJob.businessJobs;
// export const selectBusinessStats = (state) => state.adminJob.businessStats;

// export default adminJobSlice.reducer;
